import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Observation } from "@medplum/fhirtypes";

import { Table } from "@mantine/core";

import LaborStageRows from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LaborStageRows";
import { LaborStage, LaborStages } from "@/encounters/components/EncounterLabor/types/labor";
import FlowsheetTable from "@/encounters/components/FlowsheetTable/FlowsheetTable";
import { useLaborAmendFlowsheetPersist } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import { RootState } from "@/store";

interface EncounterLaborTableProps {
  showNumbersOnly?: boolean;
  isViewOnly?: boolean;
}

export default function EncounterLaborTable({
  showNumbersOnly,
  isViewOnly,
}: EncounterLaborTableProps) {
  const { clientId, encounterId } = useParams() as { clientId: string; encounterId: string };
  const stages = useSelector<RootState, Record<LaborStage, Observation>>(
    (state) => state.laborStages.entities,
  );
  const { applyObservationAmendPreview } = useLaborAmendFlowsheetPersist();

  // Get ordered stages with all history preserved
  const orderedStages = [
    LOINCAnswerCodes.SecondStageLabor,
    LOINCAnswerCodes.ActiveLabor,
    LOINCAnswerCodes.EarlyLabor,
    LOINCAnswerCodes.ProdromalLabor,
  ];

  return (
    <FlowsheetTable>
      <TableHeader />
      <Table.Tbody>
        {orderedStages.map((stageCode) => {
          const value = stageCode as LaborStage;
          const rawStageObservation = stages[value];
          const observation = rawStageObservation
            ? applyObservationAmendPreview(rawStageObservation)
            : undefined;
          return (
            <React.Fragment key={stageCode}>
              <LaborStageRows
                stage={value}
                clientId={clientId}
                flowsheetId="labor"
                isViewOnly={isViewOnly}
                encounterId={encounterId}
                observation={observation}
                label={LaborStages[value]}
                showNumbersOnly={showNumbersOnly ?? false}
                key={`${value}-${observation?.id || "none"}`}
              />
            </React.Fragment>
          );
        })}
      </Table.Tbody>
    </FlowsheetTable>
  );
}

function TableHeader() {
  return (
    <Table.Thead>
      <Table.Tr>
        <Table.Th colSpan={5} ta="center">
          VITALS
        </Table.Th>
        <Table.Th colSpan={3} ta="center">
          CONTRACTIONS
        </Table.Th>
        <Table.Th colSpan={4} ta="center">
          PROGRESS
        </Table.Th>
      </Table.Tr>
      <Table.Tr styles={{ tr: { borderWidth: "2px" } }}>
        <Table.Th ta="center">Time</Table.Th>
        <Table.Th ta="center">FHR</Table.Th>
        <Table.Th ta="center">BP</Table.Th>
        <Table.Th ta="center">Temp</Table.Th>
        <Table.Th ta="center">Pulse</Table.Th>
        <Table.Th ta="center">Freq</Table.Th>
        <Table.Th ta="center">Dur</Table.Th>
        <Table.Th ta="center">Strg</Table.Th>
        <Table.Th ta="center">Dil</Table.Th>
        <Table.Th ta="center">Eff</Table.Th>
        <Table.Th ta="center">Stat</Table.Th>
        <Table.Th ta="center">Phase</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );
}
